import * as React from 'react';
import { useState, useEffect, useReducer } from 'react';
import { MyModelsFolder, MyModelsPageState } from '../../pages/subpages/MyModels';
import apiService from '../../services/ApiService';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import ModelListFolderOptionMenu from './ModelListFolderOptionMenu';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';
import { cn } from '../../lib/utils';

const deleteModal = (folder: MyModelsFolder, state: MyModelsPageState, reloadState: Function) => {
    const parentElem = state.folders[folder.folderId.toString()]

    const handleClose = () => {
        folder.deleteModalOpen = false;
        reloadState();
    }

    const handleDelete = () => {
        apiService.deleteFolder(folder.id).then(response => {
            if (response) {
                folder.deleteModalOpen = false;
                const index = parentElem.folderChildrensIds.indexOf(folder.id);
                if (index !== -1) {
                    parentElem.folderChildrensIds.splice(index, 1);
                }
                delete state.folders[folder.id.toString()]
                reloadState();
            }
        })
    }

    return (
      <TailwindModal
        isOpen={folder.deleteModalOpen}
        onClose={handleClose}
        size="sm"
        showCloseButton={false}
      >
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-xl font-bold text-center text-slate-900">
            Are you sure?
          </h2>
          <p className="text-sm text-center text-slate-700">
            This will delete <b>{folder.name}</b> and all its content permanently.
          </p>
          <div className="flex justify-center gap-3 mt-2">
            <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleClose}>
              Cancel
            </TailwindButton>
            <TailwindButton variant="danger" icon="delete_forever" size="sm" onClick={handleDelete}>
              Yes, Delete
            </TailwindButton>
          </div>
        </div>
      </TailwindModal>
    );
  }

function RenameForm({ folder, reloadState }: { folder: MyModelsFolder, reloadState: Function }) {
    const [name, setName] = useState(folder.name);
    const [error, setError] = useState(false);

    const onSubmit = (event) => {
        event.preventDefault();
        if (!name.trim()) {
            setError(true);
            return;
        }
        if (folder.id) {
            apiService.putFolder(folder.id, name, folder.folderId).then(response => {
                if (response) {
                    folder.name = name;
                    folder.editing = false;
                    reloadState();
                }
            });
        }
    };

    return (
        <div className="flex-1 min-w-0">
            <form onSubmit={onSubmit}>
                <div className="relative flex items-center h-10 gap-1">
                    <input
                        type="text"
                        value={name}
                        className={cn(
                            "flex-1 min-w-0 ml-1.5 text-sm border-b focus:border-primary focus:outline-none bg-transparent px-1 py-0.5",
                            error ? "border-red-400" : "border-slate-300"
                        )}
                        onChange={(e) => { setName(e.target.value); if (error) setError(false); }}
                        onFocus={event => { event.target.select(); }}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500"
                        aria-label="Save"
                    >
                        <span className="material-symbols-outlined text-[18px]">done</span>
                    </button>
                    <button
                        type="button"
                        className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500"
                        onClick={() => { folder.editing = false; reloadState(); }}
                        aria-label="Cancel"
                    >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                    {error && (
                        <p className="absolute bottom-full left-0 mb-1 text-xs text-red-500 bg-white border border-red-100 rounded px-1.5 py-0.5 shadow-sm whitespace-nowrap">
                            Name cannot be empty
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
}

export default function ModelListFolder({ folder, state, reloadState, setCurrentFolder, moveFolder, moveModel }) {
        const [, forceRender] = useReducer((x: number) => x + 1, 0);
        const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
        const [{ isDragging }, drag, preview] = useDrag(() => ({
            type: 'ELEM',
            collect: (monitor) => ({
              isDragging: !!monitor.isDragging(),
            }),
            item: { ...folder, type: 'FOLDER' },
          }),
          [folder]
        )

        useEffect(() => {
            preview(getEmptyImage(), { captureDraggingState: true });
        }, [preview]);

        const [{ isOver }, drop] = useDrop(
            () => ({
              accept: 'ELEM',
              drop: (item: any, _monitor) => {
                if (item.type == 'FOLDER') {
                    moveFolder(item, folder);
                } else if (item.type == 'MODEL') {
                    moveModel(item, folder);
                }
              },
              collect: (monitor) => ({
                isOver: !!monitor.isOver()
              })
            }),
            [moveFolder, moveModel]
        )

        return (
            <div ref={drop}>
            <div ref={(folder.editing) ? null : drag} style={{ cursor: 'move', opacity: isDragging ? 0.5 : 1 }}>
            <div className="min-w-[250px] h-12 flex justify-end">
                <button
                    className="block text-left w-full h-full border-none bg-white"
                    onClick={(e) => {
                        // on doubleclick
                        if (e.detail == 2 && !folder.editing) {
                            setCurrentFolder(folder)
                        }
                    }}
                >
                <div
                className={cn(
                    "models-item-component group",
                    "flex w-full h-full items-center border-none hover:bg-slate-50 transition-colors",
                    isOver && "bg-primary/5 ring-2 ring-primary"
                )}>
                <span className="material-symbols-outlined text-[22px] ml-6 text-slate-500">folder</span>
                {
                    folder.editing ?
                    <RenameForm folder={folder} reloadState={reloadState} /> :
                    <p className="ml-3 truncate text-sm font-medium text-slate-800">{folder.name}</p>
                }
                <div className="flex-grow" />
                {
                    folder.editing ? <div></div> :
                    <div className="models-item-component-icons flex">
                        <button
                            className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500 hover:text-slate-700"
                            onClick={() => {
                                setCurrentFolder(folder);
                            }}
                        >
                            <span className="material-symbols-outlined text-[18px]">folder_open</span>
                        </button>
                        <button
                            className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500 hover:text-slate-700"
                            onClick={() => {
                                folder.editing = true;
                                forceRender();
                            }}
                        >
                            <span className="material-symbols-outlined text-[18px]">drive_file_rename_outline</span>
                        </button>
                    </div>
                }
                <button
                    className="size-8 mr-2 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500 hover:text-slate-700"
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                    }}
                >
                    <span className="material-symbols-outlined text-[18px]">more_vert</span>
                </button>
                <ModelListFolderOptionMenu folder={folder} reloadState={reloadState} setCurrentFolder={setCurrentFolder}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    anchorEl={menuAnchorEl}
                    onClose={() => setMenuAnchorEl(null)}/>
            </div>
            </button>
            </div>
            {deleteModal(folder, state, reloadState)}
            </div>
            </div>
        )
}
